/**
 * Daily close (end-of-business-day) report logic.
 *
 * Shared by the Cloud Functions (CommonJS `require`) and the browser
 * (`window.WTEDailyClose`) so the server-saved reports and the live
 * "today" preview in the provider dashboard are calculated identically.
 *
 * A business day is midnight-to-midnight in the provider's own time zone.
 *  - Sales are attributed to the day payment was received.
 *  - Completed orders are tracked separately from paid transactions.
 *  - "Received" counts orders PLACED that day.
 *  - "Open at close" lists orders that were still pending/confirmed/
 *    preparing/ready when the day ended (status reconstructed from
 *    statusHistory so a report built after midnight is still accurate).
 */
(function(root, factory) {
  const api = factory();
  if (typeof module === "object" && module && module.exports) {
    module.exports = api;
  }
  if (typeof window !== "undefined") {
    window.WTEDailyClose = api;
  } else if (root) {
    root.WTEDailyClose = api;
  }
})(typeof globalThis !== "undefined" ? globalThis : this, function() {
  "use strict";

  const SCHEMA_VERSION = 2;
  const DEFAULT_TIMEZONE = "America/St_Vincent";
  const DAY_MS = 24 * 60 * 60 * 1000;
  const OPEN_STATUSES = ["pending", "confirmed", "preparing", "ready"];
  const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const MAX_DRINKS = 20;
  const MAX_OPEN_ORDERS = 50;
  const DATE_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

  function round2(value) {
    const n = Number(value);
    return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
  }

  function toNumber(value, fallback = 0) {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  }

  function normalizeTimeZone(value, fallback = DEFAULT_TIMEZONE) {
    const raw = String(value || "").trim();
    if (!raw) return fallback;
    try {
      new Intl.DateTimeFormat("en-US", { timeZone: raw }).format(new Date());
      return raw;
    } catch (e) {
      return fallback;
    }
  }

  // Main business timezone per country, used only when a provider has not saved one.
  const COUNTRY_TIMEZONES = Object.freeze({
    VC: "America/St_Vincent", LC: "America/St_Lucia", GD: "America/Grenada", DM: "America/Dominica",
    AG: "America/Antigua", KN: "America/St_Kitts", BB: "America/Barbados", TT: "America/Port_of_Spain",
    JM: "America/Jamaica", BS: "America/Nassau", GY: "America/Guyana", BZ: "America/Belize",
    DO: "America/Santo_Domingo", PR: "America/Puerto_Rico", US: "America/New_York", CA: "America/Toronto",
    MX: "America/Mexico_City", BR: "America/Sao_Paulo", AR: "America/Argentina/Buenos_Aires",
    CL: "America/Santiago", CO: "America/Bogota", PE: "America/Lima", GB: "Europe/London",
    IE: "Europe/Dublin", PT: "Europe/Lisbon", ES: "Europe/Madrid", FR: "Europe/Paris", DE: "Europe/Berlin",
    IT: "Europe/Rome", NL: "Europe/Amsterdam", BE: "Europe/Brussels", CH: "Europe/Zurich", AT: "Europe/Vienna",
    SE: "Europe/Stockholm", NO: "Europe/Oslo", DK: "Europe/Copenhagen", FI: "Europe/Helsinki",
    PL: "Europe/Warsaw", GR: "Europe/Athens", TR: "Europe/Istanbul", IL: "Asia/Jerusalem",
    AE: "Asia/Dubai", SA: "Asia/Riyadh", QA: "Asia/Qatar", EG: "Africa/Cairo", MA: "Africa/Casablanca",
    NG: "Africa/Lagos", GH: "Africa/Accra", KE: "Africa/Nairobi", ZA: "Africa/Johannesburg",
    PK: "Asia/Karachi", IN: "Asia/Kolkata", BD: "Asia/Dhaka", TH: "Asia/Bangkok", VN: "Asia/Ho_Chi_Minh",
    MY: "Asia/Kuala_Lumpur", SG: "Asia/Singapore", ID: "Asia/Jakarta", PH: "Asia/Manila",
    CN: "Asia/Shanghai", HK: "Asia/Hong_Kong", KR: "Asia/Seoul", JP: "Asia/Tokyo",
    AU: "Australia/Sydney", NZ: "Pacific/Auckland",
  });

  /**
   * Business timezone for a provider: their saved IANA timezone, else their
   * country's main timezone, else the platform default. Server and browser
   * both use this so they agree on which business day it is.
   */
  function resolveProviderTimezone(provider) {
    const p = provider || {};
    const saved = normalizeTimeZone(p.timezone, null);
    if (saved) return saved;
    const country = String(p.country || (p.addressComponents && p.addressComponents.country) || "").trim().toUpperCase();
    return normalizeTimeZone(COUNTRY_TIMEZONES[country], DEFAULT_TIMEZONE);
  }

  function toMillis(value) {
    if (value === null || value === undefined || value === "") return NaN;
    if (typeof value === "number") return value;
    if (value instanceof Date) return value.getTime();
    if (typeof value.toMillis === "function") return value.toMillis();
    if (typeof value.toDate === "function") return value.toDate().getTime();
    if (typeof value.seconds === "number") return value.seconds * 1000 + Math.floor(toNumber(value.nanoseconds) / 1e6);
    if (typeof value._seconds === "number") return value._seconds * 1000;
    return new Date(value).getTime();
  }

  const formatterCache = new Map();
  function getPartsFormatter(timeZone) {
    if (!formatterCache.has(timeZone)) {
      formatterCache.set(timeZone, new Intl.DateTimeFormat("en-US", {
        timeZone,
        hourCycle: "h23",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }));
    }
    return formatterCache.get(timeZone);
  }

  function getZonedParts(ms, timeZone) {
    const parts = {};
    for (const part of getPartsFormatter(timeZone).formatToParts(new Date(ms))) {
      if (part.type !== "literal") parts[part.type] = Number(part.value);
    }
    if (parts.hour === 24) parts.hour = 0;
    return parts;
  }

  // Offset (ms) of the zone from UTC at the given instant.
  function getTimeZoneOffsetMs(ms, timeZone) {
    const p = getZonedParts(ms, timeZone);
    const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    return asUtc - (Math.floor(ms / 1000) * 1000);
  }

  function getLocalDateKey(value, timeZone) {
    const ms = toMillis(value);
    if (!Number.isFinite(ms)) return null;
    const p = getZonedParts(ms, normalizeTimeZone(timeZone));
    return `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
  }

  function getLocalHour(value, timeZone) {
    const ms = toMillis(value);
    if (!Number.isFinite(ms)) return null;
    return getZonedParts(ms, normalizeTimeZone(timeZone)).hour;
  }

  function parseDateKey(dateKey) {
    if (!DATE_KEY_PATTERN.test(String(dateKey || ""))) {
      throw new Error(`Invalid business date: ${dateKey}`);
    }
    const [year, month, day] = String(dateKey).split("-").map(Number);
    return { year, month, day };
  }

  function addDaysToKey(dateKey, days) {
    const { year, month, day } = parseDateKey(dateKey);
    const d = new Date(Date.UTC(year, month - 1, day + days));
    return d.toISOString().slice(0, 10);
  }

  function compareDateKeys(a, b) {
    return a < b ? -1 : (a > b ? 1 : 0);
  }

  function getDayOfWeek(dateKey) {
    const { year, month, day } = parseDateKey(dateKey);
    return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  }

  // UTC instant of local midnight at the start of dateKey in timeZone (DST-safe).
  function zonedMidnightUtc(dateKey, timeZone) {
    const { year, month, day } = parseDateKey(dateKey);
    const guess = Date.UTC(year, month - 1, day);
    let result = guess - getTimeZoneOffsetMs(guess, timeZone);
    const corrected = guess - getTimeZoneOffsetMs(result, timeZone);
    if (corrected !== result) result = corrected;
    return result;
  }

  function getBusinessDayBounds(dateKey, timeZone) {
    const zone = normalizeTimeZone(timeZone);
    const startMs = zonedMidnightUtc(dateKey, zone);
    const endMs = zonedMidnightUtc(addDaysToKey(dateKey, 1), zone);
    return { startMs, endMs, start: new Date(startMs).toISOString(), end: new Date(endMs).toISOString() };
  }

  function listDateKeys(startKey, endKey) {
    const keys = [];
    if (compareDateKeys(startKey, endKey) > 0) return keys;
    let key = startKey;
    while (compareDateKeys(key, endKey) <= 0 && keys.length < 5000) {
      keys.push(key);
      key = addDaysToKey(key, 1);
    }
    return keys;
  }

  function normalizeStatus(status) {
    const value = String(status || "pending").toLowerCase();
    return value === "canceled" ? "cancelled" : value;
  }

  function getSortedHistory(order) {
    const history = Array.isArray(order && order.statusHistory) ? order.statusHistory : [];
    return history
      .map((entry) => ({ status: normalizeStatus(entry && entry.status), ms: toMillis(entry && (entry.timestamp || entry.at || entry.createdAt)) }))
      .filter((entry) => Number.isFinite(entry.ms))
      .sort((a, b) => a.ms - b.ms);
  }

  // When the order first reached `status` (ms), or NaN if it never did.
  function getStatusReachedAt(order, status) {
    const target = normalizeStatus(status);
    const explicitField = target === "completed" ? "completedAt" : (target === "cancelled" ? "cancelledAt" : null);
    if (explicitField) {
      const explicitMs = toMillis(order && order[explicitField]);
      if (Number.isFinite(explicitMs)) return explicitMs;
    }
    const entry = getSortedHistory(order).find((item) => item.status === target);
    if (entry) return entry.ms;
    if (normalizeStatus(order && order.status) === target) {
      const updatedMs = toMillis(order.updatedAt);
      if (Number.isFinite(updatedMs)) return updatedMs;
      return toMillis(order.createdAt);
    }
    return NaN;
  }

  function getPaymentReceivedAt(order) {
    if (String(order && order.paymentStatus || "").toLowerCase() !== "paid") return NaN;
    const paidMs = toMillis(order && order.paidAt);
    if (Number.isFinite(paidMs)) return paidMs;
    return toMillis(order && order.createdAt);
  }

  // Order status as it was at instant `atMs`.
  function getStatusAt(order, atMs) {
    const createdMs = toMillis(order && order.createdAt);
    if (Number.isFinite(createdMs) && createdMs > atMs) return null;
    const history = getSortedHistory(order);
    let status = null;
    for (const entry of history) {
      if (entry.ms <= atMs) status = entry.status;
      else break;
    }
    const current = normalizeStatus(order && order.status);
    if (status) {
      // Terminal states may be missing from legacy history; trust explicit timestamps.
      for (const terminal of ["completed", "cancelled"]) {
        const reached = getStatusReachedAt(order, terminal);
        if (Number.isFinite(reached) && reached <= atMs) return terminal;
      }
      return status;
    }
    if (history.length === 0) {
      if (current === "completed" || current === "cancelled") {
        const reached = getStatusReachedAt(order, current);
        return Number.isFinite(reached) && reached <= atMs ? current : "pending";
      }
      return current;
    }
    return "pending";
  }

  function orderMealSales(order) {
    if (order.mealSubtotal !== undefined && order.mealSubtotal !== null) return toNumber(order.mealSubtotal);
    const subtotal = order.subtotal !== undefined && order.subtotal !== null ? toNumber(order.subtotal) : null;
    if (subtotal !== null) return subtotal - toNumber(order.drinkTotal);
    return toNumber(order.price) - toNumber(order.drinkTotal) - toNumber(order.taxAmount);
  }

  function itemKey(order) {
    const name = String(order.foodItem || order.itemName || "Unknown item").trim() || "Unknown item";
    return { key: order.menuItemId ? `id:${order.menuItemId}` : `name:${name.toLowerCase()}`, name };
  }

  function sortItems(list) {
    return list.sort((a, b) => (b.quantity - a.quantity) || (b.grossSales - a.grossSales) || a.name.localeCompare(b.name));
  }

  function emptyTotals() {
    return {
      ordersReceived: 0,
      ordersCompleted: 0,
      ordersPaid: 0,
      ordersCancelled: 0,
      ordersOpenAtClose: 0,
      itemsSold: 0,
      mealSales: 0,
      drinkSales: 0,
      tax: 0,
      grossSales: 0,
      averageOrderValue: 0,
      // Channel split. grossSales is the combined total once walk-in sales are applied.
      onlineSales: 0,
      walkInSales: 0,
      walkInTax: 0,
      walkInTransactions: 0,
      // Walk-in food/drink split (excl. tax); unsplit is walk-in net sales the POS didn't categorise.
      walkInMealSales: 0,
      walkInDrinkSales: 0,
      walkInUnsplitSales: 0,
      transactions: 0,
    };
  }

  // Older stored reports predate the channel fields; derive them from online data.
  function withChannelTotals(totals) {
    const t = totals || {};
    const grossSales = toNumber(t.grossSales);
    const walkInSales = toNumber(t.walkInSales);
    return {
      ...emptyTotals(),
      ...t,
      grossSales,
      onlineSales: t.onlineSales !== undefined ? toNumber(t.onlineSales) : round2(grossSales - walkInSales),
      walkInSales,
      walkInTax: toNumber(t.walkInTax),
      walkInTransactions: toNumber(t.walkInTransactions),
      walkInMealSales: toNumber(t.walkInMealSales),
      walkInDrinkSales: toNumber(t.walkInDrinkSales),
      walkInUnsplitSales: toNumber(t.walkInUnsplitSales),
      ordersPaid: t.ordersPaid !== undefined ? toNumber(t.ordersPaid) : toNumber(t.ordersCompleted),
      transactions: t.transactions !== undefined
        ? toNumber(t.transactions)
        : toNumber(t.ordersPaid !== undefined ? t.ordersPaid : t.ordersCompleted) + toNumber(t.walkInTransactions),
    };
  }

  /**
   * Build the end-of-day report for one provider and one business date.
   * @param {Array<object>} orders orders for this provider (may include other days)
   * @param {object} options { providerId, providerName, businessDate, timezone, currency, closedAt, closedBy, now }
   */
  function buildDailyCloseReport(orders, options = {}) {
    const timezone = normalizeTimeZone(options.timezone);
    const businessDate = options.businessDate;
    const { startMs, endMs, start, end } = getBusinessDayBounds(businessDate, timezone);
    const nowMs = Number.isFinite(toMillis(options.now)) ? toMillis(options.now) : Date.now();
    const evaluateAtMs = Math.min(endMs - 1, nowMs);
    const totals = emptyTotals();
    const items = new Map();
    const drinks = new Map();
    const hourlyOrders = new Array(24).fill(0);
    const openOrders = [];
    const currencyCounts = new Map();
    const seen = new Set();

    for (const order of Array.isArray(orders) ? orders : []) {
      if (!order) continue;
      const id = String(order.id || order.orderId || "");
      if (id) {
        if (seen.has(id)) continue;
        seen.add(id);
      }
      const createdMs = toMillis(order.createdAt);
      if (!Number.isFinite(createdMs) || createdMs >= endMs || createdMs > nowMs) continue;

      if (createdMs >= startMs) {
        totals.ordersReceived += 1;
        const hour = getLocalHour(createdMs, timezone);
        if (hour !== null) hourlyOrders[hour] += 1;
      }

      const completedMs = getStatusReachedAt(order, "completed");
      if (Number.isFinite(completedMs) && completedMs >= startMs && completedMs < endMs && completedMs <= nowMs) {
        totals.ordersCompleted += 1;
      }

      const paidMs = getPaymentReceivedAt(order);
      if (Number.isFinite(paidMs) && paidMs >= startMs && paidMs < endMs && paidMs <= nowMs) {
        const quantity = Math.max(1, Math.round(toNumber(order.quantity, 1)));
        const gross = toNumber(order.price);
        const meal = orderMealSales(order);
        const drink = toNumber(order.drinkTotal);
        totals.ordersPaid += 1;
        totals.itemsSold += quantity;
        totals.grossSales += gross;
        totals.mealSales += meal;
        totals.drinkSales += drink;
        totals.tax += toNumber(order.taxAmount);
        if (order.currency) {
          const code = String(order.currency).toUpperCase();
          currencyCounts.set(code, (currencyCounts.get(code) || 0) + 1);
        }

        const { key, name } = itemKey(order);
        const row = items.get(key) || { name, menuItemId: order.menuItemId ? String(order.menuItemId) : null, quantity: 0, orders: 0, grossSales: 0 };
        row.quantity += quantity;
        row.orders += 1;
        // Retain the existing field name; v2 item amounts exclude drinks and tax.
        row.grossSales += meal;
        items.set(key, row);

        const drinkName = String(order.drink || "").trim();
        if (drinkName && drinkName.toLowerCase() !== "standard" && drinkName.toLowerCase() !== "none") {
          const drinkQty = Math.max(1, Math.round(toNumber(order.drinkQuantity, quantity)));
          const drinkRow = drinks.get(drinkName.toLowerCase()) || { name: drinkName, quantity: 0, grossSales: 0 };
          drinkRow.quantity += drinkQty;
          drinkRow.grossSales += drink;
          drinks.set(drinkName.toLowerCase(), drinkRow);
        }
      }

      const cancelledMs = getStatusReachedAt(order, "cancelled");
      if (Number.isFinite(cancelledMs) && cancelledMs >= startMs && cancelledMs < endMs && cancelledMs <= nowMs) {
        totals.ordersCancelled += 1;
      }

      const statusAtClose = getStatusAt(order, evaluateAtMs);
      if (statusAtClose && OPEN_STATUSES.includes(statusAtClose)) {
        totals.ordersOpenAtClose += 1;
        if (openOrders.length < MAX_OPEN_ORDERS) {
          openOrders.push({
            orderId: id || null,
            status: statusAtClose,
            foodItem: String(order.foodItem || "").slice(0, 120),
            clientName: String(order.clientName || "").slice(0, 100),
            quantity: Math.max(1, Math.round(toNumber(order.quantity, 1))),
            price: round2(order.price),
            createdAt: new Date(createdMs).toISOString(),
            carriedOver: createdMs < startMs,
          });
        }
      }
    }

    for (const key of ["mealSales", "drinkSales", "tax", "grossSales"]) totals[key] = round2(totals[key]);
    totals.averageOrderValue = totals.ordersPaid > 0 ? round2(totals.grossSales / totals.ordersPaid) : 0;
    totals.onlineSales = totals.grossSales;
    totals.transactions = totals.ordersPaid;
    openOrders.sort((a, b) => a.createdAt.localeCompare(b.createdAt));

    let currency = options.currency ? String(options.currency).toUpperCase() : null;
    if (currencyCounts.size > 0) {
      currency = Array.from(currencyCounts.entries()).sort((a, b) => b[1] - a[1])[0][0];
    }

    const itemList = sortItems(Array.from(items.values()))
      .map((row) => ({ ...row, grossSales: round2(row.grossSales) }));
    const drinkList = Array.from(drinks.values())
      .sort((a, b) => (b.quantity - a.quantity) || (b.grossSales - a.grossSales))
      .slice(0, MAX_DRINKS)
      .map((row) => ({ ...row, grossSales: round2(row.grossSales) }));
    const isClosed = nowMs >= endMs;

    return {
      schemaVersion: SCHEMA_VERSION,
      providerId: options.providerId ? String(options.providerId) : null,
      providerName: options.providerName ? String(options.providerName).slice(0, 120) : "",
      businessDate,
      dayOfWeek: getDayOfWeek(businessDate),
      dayName: DAY_NAMES[getDayOfWeek(businessDate)],
      timezone,
      currency: currency || null,
      periodStart: start,
      periodEnd: end,
      status: isClosed ? "closed" : "open",
      closedAt: isClosed ? (options.closedAt || new Date(nowMs).toISOString()) : null,
      closedBy: isClosed ? (options.closedBy || "system") : null,
      hasActivity: totals.ordersReceived + totals.ordersCompleted + totals.ordersPaid + totals.ordersCancelled + totals.ordersOpenAtClose > 0,
      totals,
      items: itemList,
      drinks: drinkList,
      hourlyOrders,
      hourlyBasis: "ordersPlaced",
      openOrders,
    };
  }

  // Case/space/Unicode-insensitive so "Roti", " roti " and composed/decomposed accents match.
  function normalizeItemName(name) {
    let text = String(name || "");
    if (typeof text.normalize === "function") text = text.normalize("NFC");
    return text.replace(/\s+/g, " ").trim().toLowerCase();
  }

  function itemMergeKey(item) {
    return item && item.menuItemId ? `id:${item.menuItemId}` : `name:${normalizeItemName(item && item.name)}`;
  }

  function mergeItems(target, list) {
    for (const item of Array.isArray(list) ? list : []) {
      if (!item || !String(item.name || "").trim()) continue;
      const key = itemMergeKey(item);
      const row = target.get(key) || {
        name: String(item.name).trim(), menuItemId: item.menuItemId || null,
        quantity: 0, orders: 0, grossSales: 0, walkInQuantity: 0,
      };
      row.quantity += toNumber(item.quantity);
      row.orders += toNumber(item.orders);
      row.grossSales += toNumber(item.grossSales);
      row.walkInQuantity += toNumber(item.walkInQuantity);
      if (!row.menuItemId && item.menuItemId) row.menuItemId = item.menuItemId;
      target.set(key, row);
    }
  }

  function finalizeItems(map, limit) {
    return sortItems(Array.from(map.values())).slice(0, limit).map((row) => ({ ...row, grossSales: round2(row.grossSales) }));
  }

  // ---------------- Walk-in (external POS) sales ----------------
  const WALK_IN_LIMITS = Object.freeze({
    // High enough for large-denomination currencies (IDR, VND, KRW, ...).
    MAX_AMOUNT: 1000000000000,
    MAX_TRANSACTIONS: 100000,
    MAX_ITEMS: 30,
    MAX_ITEM_NAME: 80,
    MAX_NOTES: 500,
    MAX_PAST_DAYS: 366,
  });
  const WALK_IN_PAYMENT_KEYS = ["cash", "card", "other"];

  function parseMoney(value) {
    if (value === null || value === undefined || value === "") return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : NaN;
  }

  /**
   * Validate and normalize a manually entered end-of-day POS total.
   * Used by the browser form and the Cloud Function so both enforce the same rules.
   * grossSales includes tax, matching online order gross sales.
   * @returns {{ ok: boolean, errors: string[], value: object|null }}
   */
  function validateWalkInEntry(input, options = {}) {
    const errors = [];
    const src = input || {};
    const businessDate = String(src.businessDate || "").trim();
    if (!DATE_KEY_PATTERN.test(businessDate)) errors.push("Choose a valid business date.");
    if (options.todayKey && DATE_KEY_PATTERN.test(businessDate)) {
      if (compareDateKeys(businessDate, options.todayKey) > 0) errors.push("Walk-in sales can't be entered for a future date.");
      const oldest = addDaysToKey(options.todayKey, -WALK_IN_LIMITS.MAX_PAST_DAYS);
      if (compareDateKeys(businessDate, oldest) < 0) errors.push("Walk-in sales can only be entered for the last 12 months.");
    }

    const money = (value, label, { required = false } = {}) => {
      const n = parseMoney(value);
      if (n === null) {
        if (required) errors.push(`${label} is required.`);
        return 0;
      }
      if (Number.isNaN(n) || n < 0 || n > WALK_IN_LIMITS.MAX_AMOUNT) {
        errors.push(`${label} must be between 0 and ${WALK_IN_LIMITS.MAX_AMOUNT.toLocaleString("en-US")}.`);
        return 0;
      }
      return round2(n);
    };

    const grossSales = money(src.grossSales, "Total sales", { required: true });
    const tax = money(src.tax, "Tax");
    if (tax > grossSales + 0.005) errors.push("Tax can't be more than total sales.");

    let transactions = 0;
    const rawTx = src.transactions;
    if (rawTx !== null && rawTx !== undefined && rawTx !== "") {
      const n = Number(rawTx);
      if (!Number.isInteger(n) || n < 0 || n > WALK_IN_LIMITS.MAX_TRANSACTIONS) {
        errors.push("Number of sales must be a whole number of 0 or more.");
      } else {
        transactions = n;
      }
    }
    if (grossSales > 0 && transactions === 0) errors.push("Enter how many walk-in sales (receipts) made up the total.");

    const payments = {};
    const rawPayments = src.payments || {};
    let paymentTotal = 0;
    for (const key of WALK_IN_PAYMENT_KEYS) {
      payments[key] = money(rawPayments[key], `${key[0].toUpperCase()}${key.slice(1)} payments`);
      paymentTotal += payments[key];
    }
    if (paymentTotal > 0 && Math.abs(round2(paymentTotal) - grossSales) > 0.01) {
      errors.push("Cash + card + other must add up to the total sales.");
    }

    // Optional food/drink split, excluding tax. Entering one side derives the other.
    const netSales = round2(Math.max(0, grossSales - tax));
    let mealSales = money(src.mealSales, "Food sales");
    let drinkSales = money(src.drinkSales, "Drink sales");
    const mealGiven = parseMoney(src.mealSales) !== null;
    const drinkGiven = parseMoney(src.drinkSales) !== null;
    let salesSplit = false;
    if (mealGiven || drinkGiven) {
      if (mealGiven && !drinkGiven) drinkSales = round2(netSales - mealSales);
      if (drinkGiven && !mealGiven) mealSales = round2(netSales - drinkSales);
      if (mealSales < 0 || drinkSales < 0 || Math.abs(round2(mealSales + drinkSales) - netSales) > 0.01) {
        errors.push("Food + drink sales must add up to total sales minus tax.");
      } else {
        salesSplit = true;
      }
    }
    if (!salesSplit) {
      mealSales = 0;
      drinkSales = 0;
    }

    const items = [];
    const rawItems = Array.isArray(src.items) ? src.items : [];
    if (rawItems.length > WALK_IN_LIMITS.MAX_ITEMS) errors.push(`Enter at most ${WALK_IN_LIMITS.MAX_ITEMS} items.`);
    const seen = new Map();
    for (const raw of rawItems.slice(0, WALK_IN_LIMITS.MAX_ITEMS)) {
      let name = String((raw && raw.name) || "");
      if (typeof name.normalize === "function") name = name.normalize("NFC");
      name = name.replace(/\s+/g, " ").trim().slice(0, WALK_IN_LIMITS.MAX_ITEM_NAME);
      const qtyRaw = raw ? raw.quantity : null;
      const amountRaw = raw ? raw.grossSales : null;
      const blank = !name && (qtyRaw === "" || qtyRaw === null || qtyRaw === undefined) &&
        (amountRaw === "" || amountRaw === null || amountRaw === undefined);
      if (blank) continue;
      if (!name) {
        errors.push("Each item needs a name.");
        continue;
      }
      const quantity = Number(qtyRaw);
      if (!Number.isInteger(quantity) || quantity < 1 || quantity > WALK_IN_LIMITS.MAX_TRANSACTIONS) {
        errors.push(`Quantity for "${name}" must be a whole number of 1 or more.`);
        continue;
      }
      const amount = money(amountRaw, `Sales for "${name}"`);
      const key = normalizeItemName(name);
      if (seen.has(key)) {
        const existing = seen.get(key);
        existing.quantity += quantity;
        existing.grossSales = round2(existing.grossSales + amount);
      } else {
        const row = { name, quantity, grossSales: amount };
        seen.set(key, row);
        items.push(row);
      }
    }
    const itemsTotal = items.reduce((sum, item) => sum + item.grossSales, 0);
    if (itemsTotal > grossSales + 0.01) errors.push("Item sales can't add up to more than total sales.");

    const notes = String(src.notes || "").trim().slice(0, WALK_IN_LIMITS.MAX_NOTES);
    if (errors.length > 0) return { ok: false, errors, value: null };
    return {
      ok: true,
      errors: [],
      value: { businessDate, grossSales, transactions, tax, payments, salesSplit, mealSales, drinkSales, items, notes },
    };
  }

  /**
   * Combine a daily report (online orders) with that day's walk-in POS entry.
   * Returns a new report; totals.grossSales becomes the combined daily total.
   */
  function applyWalkInSales(report, entry) {
    const base = report || {};
    const totals = withChannelTotals(base.totals);
    const onlineSales = round2(totals.onlineSales);
    const onlineTax = round2(toNumber(totals.tax) - toNumber(totals.walkInTax));
    const onlineMealSales = round2(toNumber(totals.mealSales) - toNumber(totals.walkInMealSales));
    const onlineDrinkSales = round2(toNumber(totals.drinkSales) - toNumber(totals.walkInDrinkSales));
    const onlineOrders = toNumber(totals.ordersCompleted);
    const onlineItemsSold = toNumber(totals.itemsSold) -
      (Array.isArray(base.walkIn && base.walkIn.items) ? base.walkIn.items.reduce((s, i) => s + toNumber(i.quantity), 0) : 0);
    const onlineItems = Array.isArray(base.onlineItems) ? base.onlineItems : (Array.isArray(base.items) ? base.items : []);

    if (!entry) {
      if (!base.walkIn) return base;
      // Walk-in entry was removed: restore the online-only figures.
      const { walkIn, onlineItems: _ignored, ...rest } = base;
      return applyWalkInSales({ ...rest, items: onlineItems, totals: { ...totals, grossSales: onlineSales, tax: onlineTax,
        mealSales: onlineMealSales, drinkSales: onlineDrinkSales,
        itemsSold: onlineItemsSold, walkInSales: 0, walkInTax: 0, walkInTransactions: 0,
        walkInMealSales: 0, walkInDrinkSales: 0, walkInUnsplitSales: 0, transactions: onlineOrders } }, null);
    }

    const walkInSales = round2(entry.grossSales);
    const walkInTax = round2(entry.tax);
    const walkInSplit = entry.salesSplit === true;
    const walkInMealSales = walkInSplit ? round2(entry.mealSales) : 0;
    const walkInDrinkSales = walkInSplit ? round2(entry.drinkSales) : 0;
    const walkInUnsplitSales = walkInSplit ? 0 : round2(Math.max(0, walkInSales - walkInTax));
    const walkInTransactions = toNumber(entry.transactions);
    const walkInItems = (Array.isArray(entry.items) ? entry.items : []).map((item) => ({
      name: item.name, quantity: toNumber(item.quantity), orders: 0, grossSales: round2(item.grossSales), walkInQuantity: toNumber(item.quantity),
    }));
    const itemMap = new Map();
    mergeItems(itemMap, onlineItems);
    mergeItems(itemMap, walkInItems);

    const grossSales = round2(onlineSales + walkInSales);
    const transactions = onlineOrders + walkInTransactions;
    const combinedTotals = {
      ...totals,
      onlineSales,
      walkInSales,
      walkInTax,
      walkInTransactions,
      walkInMealSales,
      walkInDrinkSales,
      walkInUnsplitSales,
      mealSales: round2(onlineMealSales + walkInMealSales),
      drinkSales: round2(onlineDrinkSales + walkInDrinkSales),
      grossSales,
      tax: round2(onlineTax + walkInTax),
      itemsSold: onlineItemsSold + walkInItems.reduce((sum, item) => sum + item.quantity, 0),
      transactions,
      averageOrderValue: transactions > 0 ? round2(grossSales / transactions) : 0,
    };

    return {
      ...base,
      hasActivity: base.hasActivity === true || walkInSales > 0 || walkInTransactions > 0,
      totals: combinedTotals,
      onlineItems,
      items: finalizeItems(itemMap),
      walkIn: {
        grossSales: walkInSales,
        tax: walkInTax,
        transactions: walkInTransactions,
        payments: entry.payments || null,
        salesSplit: walkInSplit,
        mealSales: walkInMealSales,
        drinkSales: walkInDrinkSales,
        items: walkInItems.map(({ name, quantity, grossSales: amount }) => ({ name, quantity, grossSales: amount })),
        notes: entry.notes || "",
        updatedAt: entry.updatedAt || null,
        revision: toNumber(entry.revision, 1),
      },
    };
  }

  /**
   * Summarize many daily reports over a period: totals, best days, and
   * weekday patterns with what sells on those days. Uses combined
   * (online + walk-in) sales when walk-in entries have been applied.
   * @param {Array<object>} reports daily close reports
   * @param {object} options { startDate, endDate } used to count calendar days per weekday
   */
  function summarizeDailyCloses(reports, options = {}) {
    const list = (Array.isArray(reports) ? reports : [])
      .filter((r) => r && r.businessDate && r.totals)
      .map((r) => ({ ...r, totals: withChannelTotals(r.totals) }));
    const totals = emptyTotals();
    const allItems = new Map();
    const weekdays = DAY_NAMES.map((name, dayOfWeek) => ({
      dayOfWeek, name, calendarDays: 0, activeDays: 0, ordersCompleted: 0, transactions: 0,
      grossSales: 0, walkInSales: 0, items: new Map(),
    }));
    const hasSales = (r) => r.totals.transactions > 0 || r.totals.grossSales > 0;

    for (const report of list) {
      for (const key of Object.keys(totals)) {
        if (key !== "averageOrderValue") totals[key] += toNumber(report.totals[key]);
      }
      mergeItems(allItems, report.items);
      const weekday = weekdays[Number.isInteger(report.dayOfWeek) ? report.dayOfWeek : getDayOfWeek(report.businessDate)];
      if (hasSales(report)) weekday.activeDays += 1;
      weekday.ordersCompleted += report.totals.ordersCompleted;
      weekday.transactions += report.totals.transactions;
      weekday.grossSales += report.totals.grossSales;
      weekday.walkInSales += report.totals.walkInSales;
      mergeItems(weekday.items, report.items);
    }

    const sortedDates = list.map((r) => r.businessDate).sort();
    const startDate = options.startDate || sortedDates[0] || null;
    const endDate = options.endDate || sortedDates[sortedDates.length - 1] || null;
    let calendarDayCount = 0;
    if (startDate && endDate) {
      for (const key of listDateKeys(startDate, endDate)) {
        weekdays[getDayOfWeek(key)].calendarDays += 1;
        calendarDayCount += 1;
      }
    }

    for (const key of ["mealSales", "drinkSales", "tax", "grossSales", "onlineSales", "walkInSales", "walkInTax",
      "walkInMealSales", "walkInDrinkSales", "walkInUnsplitSales"]) {
      totals[key] = round2(totals[key]);
    }
    totals.averageOrderValue = totals.transactions > 0 ? round2(totals.grossSales / totals.transactions) : 0;

    const salesDays = list.filter(hasSales);
    const bestDays = salesDays
      .slice()
      .sort((a, b) => (b.totals.grossSales - a.totals.grossSales) ||
        (b.totals.transactions - a.totals.transactions) ||
        compareDateKeys(b.businessDate, a.businessDate))
      .slice(0, 5)
      .map((r) => {
        const dayOfWeek = Number.isInteger(r.dayOfWeek) ? r.dayOfWeek : getDayOfWeek(r.businessDate);
        return {
          businessDate: r.businessDate,
          dayOfWeek,
          dayName: DAY_NAMES[dayOfWeek],
          grossSales: round2(r.totals.grossSales),
          onlineSales: round2(r.totals.onlineSales),
          walkInSales: round2(r.totals.walkInSales),
          ordersPaid: r.totals.ordersPaid,
          ordersCompleted: r.totals.ordersCompleted,
          walkInTransactions: r.totals.walkInTransactions,
          transactions: r.totals.transactions,
          itemsSold: toNumber(r.totals.itemsSold),
          topItems: (Array.isArray(r.items) ? r.items : []).slice(0, 3),
        };
      });

    const weekdayRows = weekdays.map((w) => {
      const divisor = w.calendarDays || w.activeDays || 0;
      return {
        dayOfWeek: w.dayOfWeek,
        name: w.name,
        dayName: w.name,
        calendarDays: w.calendarDays,
        activeDays: w.activeDays,
        ordersCompleted: w.ordersCompleted,
        transactions: w.transactions,
        grossSales: round2(w.grossSales),
        walkInSales: round2(w.walkInSales),
        averageSales: divisor > 0 ? round2(w.grossSales / divisor) : 0,
        averageOrders: divisor > 0 ? Math.round((w.transactions / divisor) * 10) / 10 : 0,
        topItems: finalizeItems(w.items, 3),
      };
    });
    const bestWeekday = weekdayRows
      .filter((w) => w.grossSales > 0)
      .sort((a, b) => (b.averageSales - a.averageSales) || (b.grossSales - a.grossSales))[0] || null;

    return {
      startDate,
      endDate,
      calendarDays: calendarDayCount,
      reportedDays: list.length,
      salesDays: salesDays.length,
      walkInDays: list.filter((r) => r.totals.walkInSales > 0 || r.totals.walkInTransactions > 0).length,
      legacyItemReportDays: list.filter((r) => r.schemaVersion !== SCHEMA_VERSION &&
        (r.totals.ordersPaid > 0 || (Array.isArray(r.items) && r.items.length > 0))).length,
      totals,
      averageDailySales: calendarDayCount > 0 ? round2(totals.grossSales / calendarDayCount) : 0,
      bestDays,
      weekdays: weekdayRows,
      bestWeekday,
      topItems: finalizeItems(allItems, 10),
    };
  }

  function dailyCloseDocId(providerId, businessDate) {
    return `${String(providerId)}_${businessDate}`;
  }

  return {
    SCHEMA_VERSION,
    DEFAULT_TIMEZONE,
    DAY_MS,
    DAY_NAMES,
    OPEN_STATUSES,
    normalizeTimeZone,
    COUNTRY_TIMEZONES,
    resolveProviderTimezone,
    normalizeItemName,
    toMillis,
    getLocalDateKey,
    getLocalHour,
    addDaysToKey,
    compareDateKeys,
    getDayOfWeek,
    getBusinessDayBounds,
    listDateKeys,
    getStatusAt,
    getStatusReachedAt,
    getPaymentReceivedAt,
    orderMealSales,
    buildDailyCloseReport,
    summarizeDailyCloses,
    dailyCloseDocId,
    WALK_IN_LIMITS,
    WALK_IN_PAYMENT_KEYS,
    validateWalkInEntry,
    applyWalkInSales,
    withChannelTotals,
  };
});
